
import { useEffect, useState } from 'react'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import EditButton from '../components/EditButtonModal'
import DeleteButton from '../components/DeleteButtonModal'

const emptyForm = {
  type: '',
  is_electric: false,
  price: '',
  duration_months: '',
  discount_per_unit: '',
  cashback_limit: '',
  max_cashback: '',
  max_amount: '',
  referral_bonus_l1: '',
  referral_bonus_l2: '',
  referral_bonus_l3: '',
  max_vehicles: '',
  allowed_account_types: ['individual', 'company'],
  is_active: true,
  available_from: '',
  available_until: '',
  allowed_countries: null
}

const newForm = () => ({
  ...emptyForm,
  allowed_account_types: [...emptyForm.allowed_account_types]
})

const formatDateInput = (value) => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 16)
}

const parseForm = (source) => ({
  type: source.type,
  is_electric: !!source.is_electric,
  price: +source.price,
  duration_months: +source.duration_months,
  discount_per_unit: +source.discount_per_unit,
  cashback_limit: +source.cashback_limit,
  max_cashback: +source.max_cashback,
  max_amount: +source.max_amount,
  referral_bonus_l1: +source.referral_bonus_l1,
  referral_bonus_l2: +source.referral_bonus_l2,
  referral_bonus_l3: +source.referral_bonus_l3,
  max_vehicles: source.max_vehicles === '' ? null : +source.max_vehicles,
  allowed_account_types: [...source.allowed_account_types],
  is_active: !!source.is_active,
  available_from: source.available_from
    ? new Date(source.available_from).toISOString()
    : null,
  available_until: source.available_until
    ? new Date(source.available_until).toISOString()
    : null,
  allowed_countries: source.allowed_countries === null
    ? null
    : [...source.allowed_countries]
})

const AdminMembershipTypes = () => {
  const [types, setTypes] = useState([])
  const [countries, setCountries] = useState([])
  const [form, setForm] = useState(newForm)
  const [showAddForm, setShowAddForm] = useState(false)

  const [editId, setEditId] = useState(null)
  const [editForm, setEditForm] = useState(newForm)

  const fetchData = async () => {
    const res = await fetchWithAuth('/admin/membership-types')
    const data = await res.json()

    if (Array.isArray(data)) {
      data.sort((a, b) => a.id - b.id)
      setTypes(data)
    } else {
      setTypes([])
    }
  }

  const fetchCountries = async () => {
    try {
      const res = await fetchWithAuth('/countries')
      if (!res.ok) throw new Error('Chyba načítania krajín')

      const data = await res.json()
      setCountries(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Countries load error:', err)
      setCountries([])
    }
  }

  const validateForm = (source) => {
    if (!source.allowed_account_types.length) {
      alert('Vyber aspoň jeden typ používateľa.')
      return false
    }

    if (
      source.allowed_countries !== null &&
      !source.allowed_countries.length
    ) {
      alert('Vyber krajiny alebo nastav všetky krajiny.')
      return false
    }

    if (
      source.available_from &&
      source.available_until &&
      new Date(source.available_from) >= new Date(source.available_until)
    ) {
      alert('Dátum ukončenia musí byť neskorší ako začiatok.')
      return false
    }

    return true
  }

  const handleAdd = async () => {
    if (!validateForm(form)) return

    const parsed = parseForm(form)

    const res = await fetchWithAuth(
      '/admin/membership-types',
      {
        method: 'POST',
        body: JSON.stringify(parsed),
      }
    )

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      alert(err.message || 'Chyba ukladania')
      return
    }

    setForm(newForm())
    setShowAddForm(false)
    fetchData()
  }

  const handleEdit = (t) => {
    setEditId(t.id)

    setEditForm({
      type: t.type ?? '',
      is_electric: !!t.is_electric,
      price: String(t.price ?? ''),
      duration_months: String(t.duration_months ?? ''),
      discount_per_unit: String(t.discount_per_unit ?? ''),
      cashback_limit: String(t.cashback_limit ?? ''),
      max_cashback: String(t.max_cashback ?? ''),
      max_amount: String(t.max_amount ?? ''),
      referral_bonus_l1: String(t.referral_bonus_l1 ?? ''),
      referral_bonus_l2: String(t.referral_bonus_l2 ?? ''),
      referral_bonus_l3: String(t.referral_bonus_l3 ?? ''),
      max_vehicles: String(t.max_vehicles ?? ''),
      allowed_account_types: Array.isArray(t.allowed_account_types)
        ? [...t.allowed_account_types]
        : ['individual', 'company'],
      is_active: t.is_active !== false,
      available_from: formatDateInput(t.available_from),
      available_until: formatDateInput(t.available_until),
      allowed_countries: Array.isArray(t.allowed_countries)
        ? [...t.allowed_countries]
        : null
    })
  }

  const handleCancelEdit = () => {
    setEditId(null)
    setEditForm(newForm())
  }

  const handleSaveEdit = async (id) => {
    if (!validateForm(editForm)) return

    const parsed = parseForm(editForm)

    const res = await fetchWithAuth(
      `/admin/membership-types/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(parsed),
      }
    )

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      alert(err.message || 'Chyba ukladania')
      return
    }

    setEditId(null)
    setEditForm(newForm())
    fetchData()
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Zmazať predplatné?')) return

    await fetchWithAuth(
      `/admin/membership-types/${id}`,
      {
        method: 'DELETE'
      }
    )

    fetchData()
  }

  useEffect(() => {
    fetchData()
    fetchCountries()
  }, [])

  const input = (label, key, type = 'text') => (
    <div style={{ marginBottom: 6 }}>
      <label>
        <strong>{label}:</strong>
        <br />

        <input
          type={type}
	  step={type === 'number' ? 'any' : undefined}
          value={form[key]}
          onChange={(e) =>
            setForm({
              ...form,
              [key]: e.target.value
            })
          }
          style={{ width: 200 }}
        />
      </label>
    </div>
  )

  const editInput = (key, type = 'text') => (
    <input
      type={type}
      step={type === 'number' ? 'any' : undefined}
      value={editForm[key]}
      onChange={(e) =>
        setEditForm({
          ...editForm,
          [key]: e.target.value
        })
      }
      style={{
        width: '100%',
        boxSizing: 'border-box'
      }}
    />
  )

  const toggleAccountType = (source, setter, value) => {
    const selected = source.allowed_account_types.includes(value)

    setter({
      ...source,
      allowed_account_types: selected
        ? source.allowed_account_types.filter((item) => item !== value)
        : [...source.allowed_account_types, value]
    })
  }

  const toggleCountry = (source, setter, code) => {
    const selected = source.allowed_countries || []

    setter({
      ...source,
      allowed_countries: selected.includes(code)
        ? selected.filter((item) => item !== code)
        : [...selected, code]
    })
  }

  const visibilityFields = (source, setter) => (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 20,
        marginTop: 10
      }}
    >
      <div style={{ marginBottom: 6 }}>
        <label>
          <strong>Aktívne:</strong>
          <br />

          <input
            type="checkbox"
            checked={source.is_active}
            onChange={(e) =>
              setter({
                ...source,
                is_active: e.target.checked
              })
            }
          />
        </label>
      </div>

      <div style={{ marginBottom: 6 }}>
        <strong>Typ používateľa:</strong>
        <br />

        <label>
          <input
            type="checkbox"
            checked={source.allowed_account_types.includes('individual')}
            onChange={() =>
              toggleAccountType(source, setter, 'individual')
            }
          />
          Súkromná osoba
        </label>

        {' '}

        <label>
          <input
            type="checkbox"
            checked={source.allowed_account_types.includes('company')}
            onChange={() =>
              toggleAccountType(source, setter, 'company')
            }
          />
          Firma
        </label>
      </div>

      <div style={{ marginBottom: 6 }}>
        <label>
          <strong>Dostupné od:</strong>
          <br />

          <input
            type="datetime-local"
            value={source.available_from}
            onChange={(e) =>
              setter({
                ...source,
                available_from: e.target.value
              })
            }
            style={{ width: 200 }}
          />
        </label>
      </div>

      <div style={{ marginBottom: 6 }}>
        <label>
          <strong>Dostupné do:</strong>
          <br />

          <input
            type="datetime-local"
            value={source.available_until}
            onChange={(e) =>
              setter({
                ...source,
                available_until: e.target.value
              })
            }
            style={{ width: 200 }}
          />
        </label>
      </div>

      <div style={{ marginBottom: 6 }}>
        <label>
          <strong>Krajiny:</strong>
          <br />

          <select
            value={
              source.allowed_countries === null
                ? 'all'
                : 'selected'
            }
            onChange={(e) =>
              setter({
                ...source,
                allowed_countries:
                  e.target.value === 'all' ? null : []
              })
            }
            style={{ width: 200 }}
          >
            <option value="all">Všetky krajiny</option>
            <option value="selected">Vybrané krajiny</option>
          </select>
        </label>

        {source.allowed_countries !== null && (
          <div
            style={{
              maxHeight: 130,
              overflowY: 'auto',
              border: '1px solid #ccc',
              background: '#fff',
              padding: 6,
              marginTop: 4,
              width: 200,
              boxSizing: 'border-box'
            }}
          >
            {countries.map((country) => (
              <label
                key={country.code}
                style={{
                  display: 'block',
                  whiteSpace: 'nowrap'
                }}
              >
                <input
                  type="checkbox"
                  checked={source.allowed_countries.includes(country.code)}
                  onChange={() =>
                    toggleCountry(source, setter, country.code)
                  }
                />
                {country.name} ({country.code})
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #107c6b',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <h3
        style={{
          fontSize: 18,
          marginTop: 0,
          marginBottom: 10
        }}
      >
        Správa typov predplatného
      </h3>

      {!showAddForm ? (
        <button
          onClick={() => setShowAddForm(true)}
          style={{
            marginTop: 10,
            alignSelf: 'flex-start'
          }}
        >
          Pridať
        </button>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 20
            }}
          >
            {input('Názov', 'type')}

            <div style={{ marginBottom: 6 }}>
              <label>
                <strong>Typ:</strong>
                <br />

                <select
                  value={
                    form.is_electric
                      ? 'electric'
                      : 'fuel'
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      is_electric:
                        e.target.value === 'electric'
                    })
                  }
                  style={{ width: 200 }}
                >
                  <option value="fuel">
                    fuel
                  </option>

                  <option value="electric">
                    electric
                  </option>
                </select>
              </label>
            </div>

            {input('Cena (€)', 'price', 'number')}

            {input(
              'Trvanie (mesiace)',
              'duration_months',
              'number'
            )}

            {input(
              'Zľava/jednotka (€)',
              'discount_per_unit',
              'number'
            )}

            {input(
              'Mesačný CB limit (€)',
              'cashback_limit',
              'number'
            )}

            {input(
              'Max CB (€)',
              'max_cashback',
              'number'
            )}

	{input('Max množstvo (l/kWh)', 'max_amount', 'number')}
	{input('Referral bonus L1 (€)', 'referral_bonus_l1', 'number')}
	{input('Referral bonus L2 (€)', 'referral_bonus_l2', 'number')}
	{input('Referral bonus L3 (€)', 'referral_bonus_l3', 'number')}
	{input('Max vozidiel', 'max_vehicles', 'number')}

          </div>

          {visibilityFields(form, setForm)}

          <div
            style={{
              display: 'flex',
              gap: 8,
              marginTop: 10,
              alignSelf: 'flex-start'
            }}
          >
            <button onClick={handleAdd}>
              Uložiť
            </button>

            <button
              onClick={() => {
                setShowAddForm(false)
                setForm(newForm())
              }}
            >
              Zrušiť
            </button>
          </div>
        </>
      )}

      <hr
        style={{
          width: '100%'
        }}
      />

      <div
        style={{
          border: '1px solid #c7c7c7',
          borderRadius: 10,
          background: '#fff',
          flex: 1,
          minHeight: 0,
          maxWidth: '100%',
          overflow: 'auto'
        }}
      >
	<table
	  style={{
	    width: 'max-content',
	    minWidth: '100%',
	    borderCollapse: 'collapse',
	    tableLayout: 'auto'
	  }}
	>
          <thead>
            <tr
              style={{
                background: '#c8e1e1'
              }}
            >
              <th
                style={{
                  ...headerCell,
                  width: 80,
                  whiteSpace: 'nowrap'
                }}
              >
                Akcie
              </th>

              <th style={{ ...headerCell, width: 10 }}>ID</th>
              <th style={headerCell}>Názov</th>
              <th style={headerCell}>Elektrické</th>
              <th style={headerCell}>Cena</th>
              <th style={headerCell}>Trvanie</th>
              <th style={headerCell}>Zľava</th>
              <th style={headerCell}>CB limit</th>
              <th style={headerCell}>CB max</th>
	      <th style={headerCell}>Max množstvo</th>
	      <th style={headerCell}>Referral L1</th>
	      <th style={headerCell}>Referral L2</th>
	      <th style={headerCell}>Referral L3</th>
	      <th style={headerCell}>Max vozidiel</th>
              <th style={headerCell}>Aktívne</th>
              <th style={headerCell}>Používatelia</th>
              <th style={headerCell}>Krajiny</th>
              <th style={headerCell}>Od</th>
              <th style={headerCell}>Do</th>
            </tr>
          </thead>

          <tbody>
            {types.map((t) => {
              const isEditing = editId === t.id

              return (
                <tr key={t.id}>
                  <td
                    style={{
                      ...bodyCell,
                      whiteSpace: 'nowrap',
                      paddingLeft: 6,
                      paddingRight: 6
                    }}
                  >
                    {isEditing ? (
                      <>
                        <button
                          onClick={() =>
                            handleSaveEdit(t.id)
                          }
                        >
                          Uložiť
                        </button>

                        <button
                          onClick={handleCancelEdit}
                        >
                          Zrušiť
                        </button>
                      </>
                    ) : (
                      <>
                        <EditButton
                          onClick={() =>
                            handleEdit(t)
                          }
                        />

                        <DeleteButton
                          onClick={() =>
                            handleDelete(t.id)
                          }
                        />
                      </>
                    )}
                  </td>

                  <td style={bodyCell}>
                    {t.id}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('type')
                      : t.type}
                  </td>

                  <td style={bodyCell}>
                    {isEditing ? (
                      <select
                        value={
                          editForm.is_electric
                            ? 'electric'
                            : 'fuel'
                        }
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            is_electric:
                              e.target.value === 'electric'
                          })
                        }
                        style={{
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      >
                        <option value="fuel">
                          fuel
                        </option>

                        <option value="electric">
                          electric
                        </option>
                      </select>
                    ) : (
                      t.is_electric
                        ? 'Áno'
                        : 'Nie'
                    )}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('price', 'number')
                      : t.price}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('duration_months', 'number')
                      : t.duration_months}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('discount_per_unit', 'number')
                      : t.discount_per_unit}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('cashback_limit', 'number')
                      : t.cashback_limit}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('max_cashback', 'number')
                      : t.max_cashback}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('max_amount', 'number')
                      : t.max_amount}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('referral_bonus_l1', 'number')
                      : t.referral_bonus_l1}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('referral_bonus_l2', 'number')
                      : t.referral_bonus_l2}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('referral_bonus_l3', 'number')
                      : t.referral_bonus_l3}
                  </td>

                  <td style={bodyCell}>
                    {isEditing
                      ? editInput('max_vehicles', 'number')
                      : t.max_vehicles ?? '-'}
                  </td>

                  <td style={bodyCell}>
                    {t.is_active ? 'Áno' : 'Nie'}
                  </td>

                  <td style={bodyCell}>
                    {(t.allowed_account_types || []).join(', ')}
                  </td>

                  <td style={bodyCell}>
                    {t.allowed_countries === null
                      ? 'Všetky'
                      : (t.allowed_countries || []).join(', ')}
                  </td>

                  <td style={bodyCell}>
                    {t.available_from
                      ? new Date(t.available_from).toLocaleString()
                      : '-'}
                  </td>

                  <td style={bodyCell}>
                    {t.available_until
                      ? new Date(t.available_until).toLocaleString()
                      : '-'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {editId !== null && (
          <div
            style={{
              padding: 12,
              borderTop: '1px solid #c7c7c7',
              background: '#f8f8f8'
            }}
          >
            <strong>
              Pravidlá zobrazovania – predplatné ID {editId}
            </strong>

            {visibilityFields(editForm, setEditForm)}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminMembershipTypes

const headerCell = {
  textAlign: 'left',
  padding: '8px 8px',
  fontWeight: 700,
  fontSize: 13,
  whiteSpace: 'nowrap'
}

const bodyCell = {
  textAlign: 'left',
  padding: '10px 8px',
  borderTop: '1px solid #ececec',
  fontSize: 13,
  whiteSpace: 'nowrap'
}
